(() => {
  const headers = new WeakSet();
  const navs = new Set();
  const mobileNavItems = new WeakSet();
  const stickyStates = new WeakMap();
  const hoverCloseTimers = new WeakMap();
  const productRailRequests = new Map();
  const productRailGenerationRequests = new Map();
  const productRailMarkupCache = new Map();
  const productRailMarkupStoragePrefix = 'wob:header-v2-product-rail:v3:';
  const productRailHydrations = new WeakMap();
  const productRailRetryTimers = new WeakMap();
  const productRailRetryAttempts = new WeakMap();
  const productRailRetryDelaysMs = [1000, 2500, 5000, 10000];
  const productRailPageSize = 10;
  const newsletterCloseTimers = new WeakMap();
  const newsletterFocusReturn = new WeakMap();
  let navResizeObserver;
  let stickyResizeObserver;
  let documentListenersReady = false;

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const normalizeEndpoint = (endpoint) => String(endpoint || '').trim().replace(/\/+$/, '');

  const hashCacheKey = (key) => {
    let hash = 2166136261;
    const value = String(key || '');

    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
  };

  const getSessionStorage = () => {
    try {
      return window.sessionStorage || null;
    } catch (error) {
      return null;
    }
  };

  const getProductRailMarkupStorageKey = (cacheKey) => `${productRailMarkupStoragePrefix}${hashCacheKey(cacheKey)}`;

  const normalizeProductRailMarkupEntry = (entry) => {
    if (
      !entry
      || typeof entry.markup !== 'string'
      || !entry.markup.trim()
      || typeof entry.generation !== 'string'
      || !entry.generation
    ) {
      return null;
    }

    return {
      markup: entry.markup,
      generation: entry.generation
    };
  };

  const readProductRailMarkup = (cacheKey) => {
    if (productRailMarkupCache.has(cacheKey)) {
      const entry = normalizeProductRailMarkupEntry(productRailMarkupCache.get(cacheKey));

      if (entry) {
        return entry;
      }

      productRailMarkupCache.delete(cacheKey);
    }

    const storage = getSessionStorage();

    if (!storage) return null;

    try {
      const storageKey = getProductRailMarkupStorageKey(cacheKey);
      const entry = normalizeProductRailMarkupEntry(JSON.parse(storage.getItem(storageKey) || 'null'));

      if (!entry) {
        storage.removeItem(storageKey);
        return null;
      }

      productRailMarkupCache.set(cacheKey, entry);
      return entry;
    } catch (error) {
      return null;
    }
  };

  const writeProductRailMarkup = (cacheKey, markup, generation) => {
    if (!cacheKey || typeof markup !== 'string' || !markup.trim() || !generation) return;

    const entry = { markup, generation };

    productRailMarkupCache.set(cacheKey, entry);

    try {
      getSessionStorage()?.setItem(
        getProductRailMarkupStorageKey(cacheKey),
        JSON.stringify(entry)
      );
    } catch (error) {
      // Storage can be unavailable or full; the in-page cache still handles this visit.
    }
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';

    return String(url).startsWith('//') ? `${window.location.protocol}${url}` : String(url);
  };

  const withImageWidth = (url, width) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!normalizedUrl) return '';

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (error) {
      const separator = normalizedUrl.includes('?') ? '&' : '?';

      return `${normalizedUrl}${separator}width=${width}`;
    }
  };

  const moneyAmount = (money) => {
    const amount = Number(money?.amount ?? money ?? 0);

    return Number.isFinite(amount) ? amount : 0;
  };

  const formatMoney = (amount, currencyCode = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const getProductUrl = (product) => {
    if (product?.url) return product.url;
    if (product?.handle) return `/products/${encodeURIComponent(product.handle)}`;

    return '#';
  };

  const getImage = (product) => product?.featuredImage || product?.image || null;

  const getProductRailCacheKey = (rail) => {
    const endpoint = normalizeEndpoint(rail?.dataset?.siteNavProductsEndpoint);
    const enabled = rail?.dataset?.siteNavProductsEnabled === 'true';
    const collectionHandle = rail?.dataset?.siteNavProductsCollectionHandle || '';
    const collectionId = rail?.dataset?.siteNavProductsCollectionId || '';
    const collectionCount = rail?.dataset?.siteNavProductsCollectionCount || '';

    if (!enabled || !endpoint || !collectionHandle || !('fetch' in window)) return '';

    const currencyCode = window.WobWorkerMarket?.activeCurrency || 'default';

    return [endpoint, collectionId, collectionHandle, collectionCount, productRailPageSize, currencyCode, 'recommended', 'header-nav'].join('|');
  };

  const getDirectSummary = (details) => {
    return [...details.children].find((child) => child.tagName === 'SUMMARY') || null;
  };

  const setExpanded = (control, isExpanded) => {
    if (control) {
      control.setAttribute('aria-expanded', String(isExpanded));
    }
  };

  const getNewsletterModal = (header) => header?.querySelector?.('[data-header-v2-newsletter-modal]') || null;

  const getNewsletterHeaderForTrigger = (trigger) => {
    const controlledId = trigger?.getAttribute?.('aria-controls');
    const controlledModal = controlledId ? document.getElementById(controlledId) : null;
    const localHeader = controlledModal?.closest('[data-header-v2]') || trigger?.closest?.('[data-header-v2]');

    if (getNewsletterModal(localHeader)) {
      return localHeader;
    }

    return [...document.querySelectorAll('[data-header-v2]')].find((header) => getNewsletterModal(header)) || null;
  };

  const setNewsletterTriggersExpanded = (header, isExpanded) => {
    const modal = getNewsletterModal(header);

    if (!modal?.id) return;

    document.querySelectorAll('[data-header-v2-newsletter-open][aria-expanded]').forEach((trigger) => {
      const controlledId = trigger.getAttribute('aria-controls');

      if (!controlledId || controlledId === modal.id) {
        setExpanded(trigger, isExpanded);
      }
    });
  };

  const getNewsletterFocusableElements = (modal) => {
    if (!modal) return [];

    return [
      ...modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ].filter((element) => {
      return element instanceof HTMLElement && !element.hidden && element.offsetParent !== null;
    });
  };

  const trapNewsletterFocus = (event, modal) => {
    if (event.key !== 'Tab' || !modal || modal.hidden) return;

    const focusableElements = getNewsletterFocusableElements(modal);

    if (!focusableElements.length) {
      event.preventDefault();
      modal.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  };

  const setNewsletterModalOpen = (header, isOpen, shouldRestoreFocus = false, trigger = null) => {
    const modal = getNewsletterModal(header);

    if (!modal) return;

    const closeTimer = newsletterCloseTimers.get(modal);

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      newsletterCloseTimers.delete(modal);
    }

    if (isOpen) {
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const focusReturn = trigger instanceof HTMLElement ? trigger : activeElement;

      newsletterFocusReturn.set(header, focusReturn);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('header-v2-newsletter-is-open');
      document.body.classList.add('header-v2-newsletter-is-open');
      setNewsletterTriggersExpanded(header, true);
      closeAllDropdowns();
      setMobileSearchOpen(header, false);
      setDrawerOpen(header, false);

      window.requestAnimationFrame(() => {
        modal.classList.add('site-header-v2__newsletter-modal--open');
        const feedback = modal.querySelector('[data-header-v2-newsletter-feedback]');
        const focusTarget = feedback || modal.querySelector('[data-header-v2-newsletter-close]') || modal.querySelector('input') || modal;

        focusTarget.focus({ preventScroll: true });
      });
      return;
    }

    if (modal.hidden) return;

    modal.classList.remove('site-header-v2__newsletter-modal--open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('header-v2-newsletter-is-open');
    document.body.classList.remove('header-v2-newsletter-is-open');
    setNewsletterTriggersExpanded(header, false);

    newsletterCloseTimers.set(
      modal,
      window.setTimeout(() => {
        modal.hidden = true;
        newsletterCloseTimers.delete(modal);
      }, 180)
    );

    if (shouldRestoreFocus) {
      const focusReturn = newsletterFocusReturn.get(header);

      if (focusReturn?.isConnected) {
        focusReturn.focus({ preventScroll: true });
      }
    }
  };

  const getTriggers = (nav) => [...nav.querySelectorAll('[data-site-nav-trigger]')];

  const getDropdown = (trigger) => {
    const id = trigger.getAttribute('aria-controls');

    return id ? document.getElementById(id) : null;
  };

  const canUseHoverNav = (event = null) => {
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const hasHoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const pointerCanHover = !event?.pointerType || event.pointerType === 'mouse' || event.pointerType === 'pen';

    return isDesktop && hasHoverPointer && pointerCanHover;
  };

  const canUseDropdownNav = () => window.matchMedia('(min-width: 990px)').matches;

  const clearHoverClose = (nav) => {
    const timer = hoverCloseTimers.get(nav);

    if (timer) {
      window.clearTimeout(timer);
      hoverCloseTimers.delete(nav);
    }
  };

  const updateNav = (nav) => {
    const scroller = nav.querySelector('.site-nav__scroller');
    const track = nav.querySelector('.site-nav__scrollbar');
    const thumb = nav.querySelector('.site-nav__scrollbar-thumb');

    if (!scroller || !track || !thumb) {
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    if (maxScroll <= 1) {
      nav.classList.remove('site-nav--scrollable', 'site-nav--at-start', 'site-nav--at-end');
      thumb.style.removeProperty('--nav-scrollbar-thumb-width');
      thumb.style.removeProperty('--nav-scrollbar-thumb-offset');
      return;
    }

    const trackWidth = track.clientWidth || scroller.clientWidth;
    const thumbWidth = Math.max((scroller.clientWidth / scroller.scrollWidth) * trackWidth, 24);
    const maxOffset = Math.max(trackWidth - thumbWidth, 0);
    const offset = (scroller.scrollLeft / maxScroll) * maxOffset;

    nav.classList.add('site-nav--scrollable');
    nav.classList.toggle('site-nav--at-start', scroller.scrollLeft <= 1);
    nav.classList.toggle('site-nav--at-end', maxScroll - scroller.scrollLeft <= 1);
    thumb.style.setProperty('--nav-scrollbar-thumb-width', `${thumbWidth}px`);
    thumb.style.setProperty('--nav-scrollbar-thumb-offset', `${offset}px`);
  };

  const queueUpdateNav = (nav) => {
    window.requestAnimationFrame(() => updateNav(nav));
  };

  const renderWorkerProductCard = (product, index) => {
    const image = getImage(product);
    const currencyCode = product?.priceRange?.minVariantPrice?.currencyCode || product?.price?.currencyCode || 'USD';
    const price = moneyAmount(product?.priceRange?.minVariantPrice ?? product?.price);
    const compareAtPrice = moneyAmount(product?.compareAtPriceRange?.maxVariantPrice);
    const maxPrice = moneyAmount(product?.priceRange?.maxVariantPrice ?? price);
    const productId = product.numericId || String(product.id || '').match(/(\d+)$/)?.[1] || '';
    const priceVariant = (product?.variants || []).find((variant) => moneyAmount(variant?.price) === price);
    const variantId = String(priceVariant?.id || priceVariant?.numericId || product?.selectedVariantId || '').match(/(\d+)$/)?.[1] || '';
    const onSale = compareAtPrice > price;
    const showFrom = maxPrice > price;

    return `
      <a
        class="site-nav__product-card"
        href="${escapeHtml(getProductUrl(product))}"
        data-site-nav-product-card
        data-site-nav-product-source="worker"
        data-site-nav-product-position="${index + 1}"
        data-site-nav-product-id="${escapeHtml(product.id || '')}"
        data-site-nav-product-numeric-id="${escapeHtml(product.numericId || '')}"
        data-site-nav-product-handle="${escapeHtml(product.handle || '')}"
        data-site-nav-product-title="${escapeHtml(product.title || '')}"
      >
        <span class="site-nav__product-image">
          ${image?.url ? `
            <img
              data-product-image
              src="${escapeHtml(withImageWidth(image.url, 320))}"
              srcset="
                ${escapeHtml(withImageWidth(image.url, 180))} 180w,
                ${escapeHtml(withImageWidth(image.url, 320))} 320w,
                ${escapeHtml(withImageWidth(image.url, 360))} 360w
              "
              sizes="170px"
              width="${image.width || 320}"
              height="${image.height || 480}"
              alt="${escapeHtml(image.altText || image.alt || product.title || 'Product')}"
              loading="lazy"
              decoding="async"
            >
          ` : ''}
        </span>
        <span class="site-nav__product-title">${escapeHtml(product.title || 'Product')}</span>
        <span class="site-nav__product-price price${onSale ? ' price--on-sale' : ''}" data-product-price data-product-id="${escapeHtml(productId)}" data-variant-id="${escapeHtml(variantId)}" data-base-price-cents="${Math.round(price * 100)}" data-compare-at-price-cents="${Math.round((onSale ? compareAtPrice : 0) * 100)}" data-price-show-from="${showFrom ? 'true' : 'false'}">${price > 0 ? `${onSale ? `<span class="visually-hidden" data-price-compare-label>Regular price</span><s class="price__compare" data-price-compare>${formatMoney(compareAtPrice, currencyCode)}</s>` : ''}<span class="visually-hidden" data-price-current-label>${onSale ? 'Sale price' : 'Regular price'}</span><span class="price__current" data-price-current>${showFrom ? 'From ' : ''}${formatMoney(price, currencyCode)}</span>` : ''}</span>
      </a>
    `;
  };

  const renderWorkerProductCards = (products) => {
    return products.slice(0, productRailPageSize).map(renderWorkerProductCard).join('');
  };

  const renderProductRailSkeletonCards = (count = productRailPageSize) => {
    return Array.from({ length: count }, (_, index) => `
      <div
        class="site-nav__product-card site-nav__product-card--skeleton"
        data-site-nav-product-skeleton="${index + 1}"
        aria-hidden="true"
      >
        <span class="site-nav__product-image product-card__skeleton-media"></span>
        <span class="site-nav__product-skeleton-title">
          <span class="product-card__skeleton-line product-card__skeleton-line--title"></span>
          <span class="product-card__skeleton-line site-nav__product-skeleton-line--short"></span>
        </span>
        <span class="product-card__skeleton-line product-card__skeleton-line--price"></span>
      </div>
    `).join('');
  };

  const updateDropdownProductRailState = (rail, grid) => {
    if (!rail || !grid) return;

    const maxScroll = grid.scrollWidth - grid.clientWidth;
    const isScrollable = maxScroll > 1;

    rail.classList.toggle('site-nav__dropdown-products--scrollable', isScrollable);
    rail.classList.toggle('site-nav__dropdown-products--at-start', !isScrollable || grid.scrollLeft <= 1);
    rail.classList.toggle('site-nav__dropdown-products--at-end', !isScrollable || maxScroll - grid.scrollLeft <= 1);
  };

  const showDropdownProductSkeletons = (rail, grid, status) => {
    rail.hidden = false;
    rail.classList.add('site-nav__dropdown-products--loading');
    rail.setAttribute('aria-busy', 'true');
    if (status) status.hidden = false;

    if (grid.dataset.siteNavProductsCacheKey === 'loading') {
      updateDropdownProductRailState(rail, grid);
      return;
    }

    grid.innerHTML = renderProductRailSkeletonCards();
    grid.dataset.siteNavProductsCacheKey = 'loading';
    grid.scrollLeft = 0;
    window.requestAnimationFrame(() => updateDropdownProductRailState(rail, grid));
  };

  const requestWorkerProducts = (rail) => {
    const cacheKey = getProductRailCacheKey(rail);
    const endpoint = normalizeEndpoint(rail.dataset.siteNavProductsEndpoint);
    const collectionHandle = rail.dataset.siteNavProductsCollectionHandle || '';
    const collectionId = rail.dataset.siteNavProductsCollectionId || '';
    const collectionCount = Number.parseInt(rail.dataset.siteNavProductsCollectionCount || '', 10);

    if (!cacheKey) return Promise.resolve([]);
    if (productRailRequests.has(cacheKey)) return productRailRequests.get(cacheKey);

    const request = fetch(`${endpoint}/collections/products`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        collectionId,
        collectionHandle,
        pageSize: productRailPageSize,
        sortKey: 'recommended',
        filters: [],
        source: 'header-nav'
      })
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);

        if (!response.ok || !payload?.ok || payload?.fallback?.recommended) {
          throw new Error(payload?.fallback?.reason || 'header-nav-worker-unavailable');
        }

        const products = Array.isArray(payload.products) ? payload.products : [];

        if (products.length === 0 && Number.isFinite(collectionCount) && collectionCount > 0) {
          throw new Error('header-nav-worker-empty');
        }

        const localizedProducts = await (window.WobWorkerMarket?.localizeProducts?.(products) || Promise.resolve(products));

        return {
          products: localizedProducts,
          generation: String(payload.collectionGeneration || '')
        };
      });

    productRailRequests.set(cacheKey, request);
    request.then(
      () => productRailRequests.delete(cacheKey),
      () => productRailRequests.delete(cacheKey)
    );

    return request;
  };

  const requestCollectionGeneration = (rail) => {
    const cacheKey = getProductRailCacheKey(rail);
    const endpoint = normalizeEndpoint(rail.dataset.siteNavProductsEndpoint);
    const collectionHandle = rail.dataset.siteNavProductsCollectionHandle || '';
    const collectionId = rail.dataset.siteNavProductsCollectionId || '';

    if (!cacheKey || !endpoint) return Promise.resolve('');
    if (productRailGenerationRequests.has(cacheKey)) return productRailGenerationRequests.get(cacheKey);

    const url = new URL(`${endpoint}/collections/generation`);
    if (collectionId) url.searchParams.set('collectionId', collectionId);
    if (collectionHandle) url.searchParams.set('collectionHandle', collectionHandle);

    const request = fetch(url.toString(), {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(async (response) => {
      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || 'header-nav-generation-unavailable');
      }

      return String(payload.generation || '');
    });

    productRailGenerationRequests.set(cacheKey, request);
    request.then(
      () => productRailGenerationRequests.delete(cacheKey),
      () => productRailGenerationRequests.delete(cacheKey)
    );

    return request;
  };

  const clearDropdownProductRetry = (rail) => {
    const timer = productRailRetryTimers.get(rail);

    if (timer !== undefined) {
      window.clearTimeout(timer);
      productRailRetryTimers.delete(rail);
    }

    productRailRetryAttempts.delete(rail);
  };

  const scheduleDropdownProductRetry = (dropdown, rail) => {
    if (!dropdown || !rail || productRailRetryTimers.has(rail)) return;

    const attempt = productRailRetryAttempts.get(rail) || 0;
    const delay = productRailRetryDelaysMs[Math.min(attempt, productRailRetryDelaysMs.length - 1)];
    const timer = window.setTimeout(() => {
      productRailRetryTimers.delete(rail);

      if (!dropdown.hidden && rail.dataset.siteNavProductsLoaded !== 'true') {
        hydrateDropdownProducts(dropdown);
      }
    }, delay);

    productRailRetryAttempts.set(rail, attempt + 1);
    productRailRetryTimers.set(rail, timer);
  };

  const renderDropdownProducts = (dropdown, rail, grid, status, markup, cacheKey) => {
    clearDropdownProductRetry(rail);
    rail.classList.remove('site-nav__dropdown-products--loading');
    rail.removeAttribute('aria-busy');
    rail.dataset.siteNavProductsLoaded = 'true';
    if (status) status.hidden = true;

    if (!markup) {
      rail.hidden = true;
      grid.innerHTML = '';
      grid.dataset.siteNavProductsCacheKey = cacheKey;
      return;
    }

    if (grid.dataset.siteNavProductsCacheKey !== cacheKey) {
      grid.innerHTML = markup;
      grid.dataset.siteNavProductsCacheKey = cacheKey;
    }

    rail.hidden = false;

    queueDropdownProductsUpdate(dropdown);
  };

  const hydrateDropdownProducts = (dropdown) => {
    const rail = dropdown?.querySelector('[data-site-nav-products]');
    const grid = dropdown?.querySelector('[data-site-nav-products-grid]');
    const status = dropdown?.querySelector('[data-site-nav-products-status]');

    if (!rail || !grid || rail.dataset.siteNavProductsSource !== 'worker') return;
    if (rail.dataset.siteNavProductsLoaded === 'true') return;
    if (productRailRetryTimers.has(rail)) return;

    const cacheKey = getProductRailCacheKey(rail);

    if (!cacheKey) {
      rail.hidden = true;
      rail.classList.remove('site-nav__dropdown-products--loading');
      rail.removeAttribute('aria-busy');
      grid.innerHTML = '';
      return;
    }

    const cachedEntry = readProductRailMarkup(cacheKey);

    if (cachedEntry !== null) {
      renderDropdownProducts(dropdown, rail, grid, status, cachedEntry.markup, cacheKey);

      requestCollectionGeneration(rail)
        .then((generation) => {
          if (!generation || generation === cachedEntry.generation) return null;

          return requestWorkerProducts(rail).then(({ products, generation: productGeneration }) => {
            const currentGeneration = productGeneration || generation;
            const markup = renderWorkerProductCards(products);

            writeProductRailMarkup(cacheKey, markup, currentGeneration);
            renderDropdownProducts(dropdown, rail, grid, status, markup, cacheKey);
            return null;
          });
        })
        .catch(() => {
          // Keep the last complete rail when generation validation is temporarily unavailable.
        });

      return;
    }

    showDropdownProductSkeletons(rail, grid, status);

    if (productRailHydrations.has(rail)) {
      return;
    }

    const hydration = requestWorkerProducts(rail)
      .then(({ products, generation }) => {
        const markup = renderWorkerProductCards(products);

        writeProductRailMarkup(cacheKey, markup, generation);
        renderDropdownProducts(dropdown, rail, grid, status, markup, cacheKey);
      })
      .catch(() => {
        rail.hidden = true;
        rail.classList.remove('site-nav__dropdown-products--loading');
        rail.removeAttribute('aria-busy');
        grid.innerHTML = '';
        delete grid.dataset.siteNavProductsCacheKey;
        if (status) status.hidden = true;
        scheduleDropdownProductRetry(dropdown, rail);
      })
      .finally(() => {
        productRailHydrations.delete(rail);
      });

    productRailHydrations.set(rail, hydration);
  };

  const updateDropdownProducts = (dropdown) => {
    if (!dropdown || dropdown.hidden) {
      return;
    }

    const rail = dropdown.querySelector('[data-site-nav-products]');
    const grid = dropdown.querySelector('[data-site-nav-products-grid]');
    const cards = [...dropdown.querySelectorAll('[data-site-nav-product-card]')];

    if (rail?.dataset.siteNavProductsSource === 'worker' && rail.dataset.siteNavProductsLoaded !== 'true') {
      hydrateDropdownProducts(dropdown);
      return;
    }

    if (!rail || !grid || cards.length === 0) {
      return;
    }

    rail.hidden = false;
    cards.forEach((card) => {
      card.hidden = false;
    });

    updateDropdownProductRailState(rail, grid);
  };

  const queueDropdownProductsUpdate = (dropdown) => {
    window.requestAnimationFrame(() => updateDropdownProducts(dropdown));
  };

  const queueOpenDropdownProductsUpdate = (nav) => {
    nav.querySelectorAll('[data-site-nav-dropdown]:not([hidden])').forEach(queueDropdownProductsUpdate);
  };

  const setDropdownState = (trigger, isOpen) => {
    const dropdown = getDropdown(trigger);

    setExpanded(trigger, isOpen);

    if (dropdown) {
      dropdown.hidden = !isOpen;

      if (isOpen) {
        queueDropdownProductsUpdate(dropdown);
      }
    }
  };

  const closeDropdowns = (nav, shouldRestoreFocus = false) => {
    const activeTrigger = getTriggers(nav).find((trigger) => trigger.getAttribute('aria-expanded') === 'true');

    clearHoverClose(nav);
    getTriggers(nav).forEach((trigger) => setDropdownState(trigger, false));
    nav.classList.remove('site-nav--dropdown-open');

    if (shouldRestoreFocus) {
      activeTrigger?.focus();
    }
  };

  const closeAllDropdowns = (exceptNav = null) => {
    navs.forEach((nav) => {
      if (nav !== exceptNav) {
        closeDropdowns(nav);
      }
    });
  };

  const openDropdown = (nav, trigger) => {
    closeAllDropdowns(nav);
    getTriggers(nav).forEach((item) => setDropdownState(item, item === trigger));
    nav.classList.add('site-nav--dropdown-open');
    queueOpenDropdownProductsUpdate(nav);
  };

  const scheduleHoverClose = (nav) => {
    clearHoverClose(nav);

    hoverCloseTimers.set(
      nav,
      window.setTimeout(() => {
        hoverCloseTimers.delete(nav);

        if (!nav.matches(':hover') && !nav.contains(document.activeElement)) {
          closeDropdowns(nav);
        }
      }, 140)
    );
  };

  const getWheelDelta = (event, element) => {
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;

    if (event.deltaMode === 1) {
      return delta * 16;
    }

    if (event.deltaMode === 2) {
      return delta * element.clientWidth;
    }

    return delta;
  };

  const handleNavWheel = (event, nav) => {
    if (event.target.closest('[data-site-nav-dropdown]')) {
      return;
    }

    const scroller = nav.querySelector('.site-nav__scroller');

    if (!scroller) {
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    if (maxScroll <= 1) {
      return;
    }

    const delta = getWheelDelta(event, scroller);

    if (!delta) {
      return;
    }

    event.preventDefault();

    const nextScroll = Math.max(0, Math.min(maxScroll, scroller.scrollLeft + delta));

    if (nextScroll === scroller.scrollLeft) {
      return;
    }

    closeDropdowns(nav);
    scroller.scrollLeft = nextScroll;
    queueUpdateNav(nav);
  };

  const handleProductWheel = (event, grid) => {
    const maxScroll = grid.scrollWidth - grid.clientWidth;

    if (maxScroll <= 1) {
      return;
    }

    const delta = getWheelDelta(event, grid);

    if (!delta) {
      return;
    }

    event.preventDefault();

    const nextScroll = Math.max(0, Math.min(maxScroll, grid.scrollLeft + delta));

    if (nextScroll === grid.scrollLeft) {
      return;
    }

    grid.scrollLeft = nextScroll;
    queueDropdownProductsUpdate(grid.closest('[data-site-nav-dropdown]'));
  };

  const syncDrawerItems = (container) => {
    container.querySelectorAll('[data-header-v2-drawer-item]').forEach((item) => {
      setExpanded(getDirectSummary(item), item.open);
    });
  };

  const closeDrawerItems = (container) => {
    container.querySelectorAll('[data-header-v2-drawer-item][open]').forEach((item) => {
      item.open = false;
    });

    syncDrawerItems(container);
  };

  const setDrawerOpen = (header, isOpen, shouldRestoreFocus = false) => {
    const drawer = header.querySelector('[data-header-v2-drawer]');
    const toggle = header.querySelector('[data-header-v2-menu-toggle]');

    if (!drawer) {
      return;
    }

    drawer.hidden = !isOpen;
    setExpanded(toggle, isOpen);
    toggle?.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    document.documentElement.classList.toggle('header-v2-drawer-is-open', isOpen);
    document.body.classList.toggle('header-v2-drawer-is-open', isOpen);

    if (isOpen) {
      closeAllDropdowns();
      setMobileSearchOpen(header, false);
      setNewsletterModalOpen(header, false);
      drawer.querySelector('[data-header-v2-drawer-close]')?.focus();
      return;
    }

    closeDrawerItems(drawer);

    if (shouldRestoreFocus) {
      toggle?.focus();
    }
  };

  const setMobileSearchOpen = (header, isOpen, shouldFocus = false) => {
    const panel = header.querySelector('[data-header-v2-mobile-search]');
    const toggle = header.querySelector('[data-header-v2-search-toggle]');

    if (!panel || !toggle) {
      return;
    }

    panel.hidden = !isOpen;
    setExpanded(toggle, isOpen);
    toggle?.setAttribute('aria-label', isOpen ? 'Close search' : 'Search');
    document.documentElement.classList.toggle('header-v2-search-is-open', isOpen);
    document.body.classList.toggle('header-v2-search-is-open', isOpen);

    if (isOpen) {
      closeAllDropdowns();
      setDrawerOpen(header, false);
      setNewsletterModalOpen(header, false);
      // Reveal Liquid-rendered terms and product skeletons immediately. The
      // deferred search bundle hydrates this same panel when it becomes ready.
      panel.querySelectorAll('[data-header-search]').forEach((form) => {
        form.classList.add('header-search-is-open');
        form.dataset.headerSearchRequestedOpen = 'true';
      });
      panel.querySelectorAll('[data-header-search-dropdown]').forEach((dropdown) => {
        dropdown.hidden = false;
      });
      panel.querySelectorAll('[data-header-search-input]').forEach((input) => {
        input.setAttribute('aria-expanded', 'true');
      });
      panel.querySelectorAll('[data-header-search-close]').forEach((button) => {
        button.hidden = false;
      });

      if (shouldFocus) {
        const input = panel.querySelector('[data-header-search-input]');
        const form = input?.closest('[data-header-search]');

        // Start hydration before asking WebKit to focus a newly revealed,
        // fixed-position input. Safari may settle that focus asynchronously.
        form?.dispatchEvent(new CustomEvent('wob:header-search:open'));
        input?.focus({ preventScroll: true });

        if (input && document.activeElement !== input) {
          window.requestAnimationFrame(() => input.focus({ preventScroll: true }));
        }
      }

      return;
    }

    if (document.activeElement instanceof HTMLElement && panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    panel.querySelectorAll('[data-header-search]').forEach((form) => {
      form.classList.remove('header-search-is-open');
      delete form.dataset.headerSearchRequestedOpen;
    });
    panel.querySelectorAll('[data-header-search-dropdown]').forEach((dropdown) => {
      dropdown.hidden = true;
    });
    panel.querySelectorAll('[data-header-search-input]').forEach((input) => {
      input.setAttribute('aria-expanded', 'false');
    });
    panel.querySelectorAll('[data-header-search-close]').forEach((button) => {
      button.hidden = true;
    });

    if (shouldFocus) {
      toggle?.focus();
    }
  };

  const isHeaderInteractive = (header) => {
    const activeElement = document.activeElement;
    const drawer = header.querySelector('[data-header-v2-drawer]');
    const mobileSearch = header.querySelector('[data-header-v2-mobile-search]');
    const newsletterModal = getNewsletterModal(header);

    return Boolean(
      (activeElement && activeElement !== document.body && header.contains(activeElement)) ||
        !drawer?.hidden ||
        !mobileSearch?.hidden ||
        !newsletterModal?.hidden
    );
  };

  const isStickyEnabled = (header) => {
    const bar = header.querySelector('[data-header-v2-sticky-bar]');

    return Boolean(bar?.classList.contains('site-header-v2__sticky--enabled'));
  };

  const updateHeaderTotalHeight = (header) => {
    const announcementHeight = [...document.querySelectorAll('[data-announcement-bar]')]
      .reduce((total, bar) => {
        const rect = bar.getBoundingClientRect();

        return rect.height > 0 ? total + rect.height : total;
      }, 0);
    const height = Math.ceil(header.getBoundingClientRect().height + announcementHeight);

    if (height > 0) {
      document.documentElement.style.setProperty('--site-header-total-height', `${height}px`);
    }
  };

  const resetStickyState = (header) => {
    const bar = header.querySelector('[data-header-v2-sticky-bar]');
    const state = stickyStates.get(header);

    header.classList.remove('site-header-v2--sticky-fixed');
    header.style.removeProperty('--header-v2-sticky-height');
    bar?.classList.remove(
      'site-header-v2__sticky--fixed',
      'site-header-v2__sticky--collapsed',
      'site-header-v2__sticky--stuck'
    );
    document.documentElement.style.setProperty('--site-header-offset', '0px');
    document.documentElement.style.setProperty('--site-header-visible-offset', '0px');
    updateHeaderTotalHeight(header);

    if (state) {
      state.threshold = null;
      state.ticking = false;
    }
  };

  // Attentive temporarily positions the document/body and offsets it by the
  // saved scroll position. Those coordinates are not normal document geometry.
  const isDocumentScrollLocked = () => [document.documentElement, document.body].some((element) => {
    const style = window.getComputedStyle(element);
    return (style.position === 'absolute' || style.position === 'fixed')
      && (style.overflowY === 'hidden' || style.overflowY === 'clip');
  });

  const updateStickyHeader = (header) => {
    const bar = header.querySelector('[data-header-v2-sticky-bar]');
    const state = stickyStates.get(header);

    if (!bar || !state || !isStickyEnabled(header)) {
      resetStickyState(header);
      return;
    }

    if (isDocumentScrollLocked()) {
      state.ticking = false;
      return;
    }

    const currentScroll = Math.max(window.scrollY, 0);
    const barHeight = Math.round(bar.offsetHeight);

    if (!bar.classList.contains('site-header-v2__sticky--fixed')) {
      state.threshold = Math.round(bar.getBoundingClientRect().top + currentScroll);
    }

    const threshold = state.threshold ?? Math.round(bar.getBoundingClientRect().top + currentScroll);
    const isFixed = currentScroll >= threshold;
    const canCollapse = !isHeaderInteractive(header);
    const delta = currentScroll - state.lastScroll;

    header.style.setProperty('--header-v2-sticky-height', `${barHeight}px`);
    document.documentElement.style.setProperty('--site-header-offset', `${isFixed ? barHeight : 0}px`);
    header.classList.toggle('site-header-v2--sticky-fixed', isFixed);
    bar.classList.toggle('site-header-v2__sticky--fixed', isFixed);
    bar.classList.toggle('site-header-v2__sticky--stuck', isFixed && currentScroll > threshold + 2);

    if (!isFixed || currentScroll <= threshold + 2 || !canCollapse) {
      bar.classList.remove('site-header-v2__sticky--collapsed');
    } else if (Math.abs(delta) >= 6) {
      bar.classList.toggle(
        'site-header-v2__sticky--collapsed',
        delta > 0 && currentScroll > threshold + barHeight + 24
      );
    }

    if (bar.classList.contains('site-header-v2__sticky--collapsed')) {
      closeAllDropdowns();
    }

    document.documentElement.style.setProperty(
      '--site-header-visible-offset',
      `${isFixed && !bar.classList.contains('site-header-v2__sticky--collapsed') ? barHeight : 0}px`
    );
    updateHeaderTotalHeight(header);

    state.lastScroll = currentScroll;
    state.ticking = false;
  };

  const requestStickyUpdate = (header) => {
    const state = stickyStates.get(header);

    if (!state || state.ticking) {
      return;
    }

    state.ticking = true;
    window.requestAnimationFrame(() => updateStickyHeader(header));
  };

  const bindStickyHeader = (header) => {
    if (stickyStates.has(header)) {
      return;
    }

    stickyStates.set(header, {
      lastScroll: Math.max(window.scrollY, 0),
      threshold: null,
      ticking: false
    });

    window.addEventListener('scroll', () => requestStickyUpdate(header), { passive: true });
    window.addEventListener('resize', () => {
      if (isDocumentScrollLocked()) return;
      resetStickyState(header);
      requestStickyUpdate(header);
    }, { passive: true });

    let documentScrollLocked = isDocumentScrollLocked();
    const scrollLockObserver = new MutationObserver(() => {
      const locked = isDocumentScrollLocked();
      if (locked === documentScrollLocked) return;
      documentScrollLocked = locked;

      if (!locked) {
        // Wait until the popup has restored both root styles and scroll position.
        window.requestAnimationFrame(() => {
          if (isDocumentScrollLocked()) return;
          resetStickyState(header);
          requestStickyUpdate(header);
        });
      }
    });
    [document.documentElement, document.body].forEach((element) => {
      scrollLockObserver.observe(element, { attributes: true, attributeFilter: ['style', 'class'] });
    });

    if ('ResizeObserver' in window) {
      if (!stickyResizeObserver) {
        stickyResizeObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const observedHeader = entry.target.closest?.('[data-header-v2]') || entry.target;

            if (observedHeader?.matches?.('[data-header-v2]')) {
              requestStickyUpdate(observedHeader);
            }
          });
        });
      }

      stickyResizeObserver.observe(header);
    }

    requestStickyUpdate(header);
    updateHeaderTotalHeight(header);
  };

  const bindDocumentListeners = () => {
    if (documentListenersReady) {
      return;
    }

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) {
        return;
      }

      const newsletterTrigger = target.closest('[data-header-v2-newsletter-open]');

      if (newsletterTrigger) {
        const header = getNewsletterHeaderForTrigger(newsletterTrigger);

        if (header) {
          event.preventDefault();
          setNewsletterModalOpen(header, true, false, newsletterTrigger);
          return;
        }
      }

      if (!target.closest('.site-nav')) {
        closeAllDropdowns();
      }

      document.querySelectorAll('[data-header-v2]').forEach((header) => {
        if (!header.contains(target)) {
          setMobileSearchOpen(header, false);
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeAllDropdowns(null, true);
        document.querySelectorAll('[data-header-v2]').forEach((header) => {
          setMobileSearchOpen(header, false);
          setDrawerOpen(header, false, true);
          setNewsletterModalOpen(header, false, true);
        });
      }
    });

    documentListenersReady = true;
  };

  const initDrawerItem = (item) => {
    if (mobileNavItems.has(item)) {
      return;
    }

    mobileNavItems.add(item);
    setExpanded(getDirectSummary(item), item.open);

    item.addEventListener('toggle', (event) => {
      if (event.target === item) {
        syncDrawerItems(item.closest('[data-header-v2-drawer]') || item);
      }
    });
  };

  const initNav = (nav) => {
    if (navs.has(nav)) {
      return;
    }

    navs.add(nav);
    nav.querySelector('.site-nav__scroller')?.addEventListener('scroll', () => queueUpdateNav(nav), { passive: true });
    nav.addEventListener('wheel', (event) => handleNavWheel(event, nav), { passive: false });
    nav.addEventListener('pointerenter', (event) => {
      if (canUseHoverNav(event)) {
        clearHoverClose(nav);
      }
    });
    nav.addEventListener('pointerleave', (event) => {
      if (canUseHoverNav(event)) {
        scheduleHoverClose(nav);
      }
    });
    nav.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDropdowns(nav, true);
      }
    });
    nav.addEventListener('focusout', (event) => {
      if (!event.relatedTarget || !nav.contains(event.relatedTarget)) {
        closeDropdowns(nav);
      }
    });

    getTriggers(nav).forEach((trigger) => {
      setDropdownState(trigger, false);
      trigger.addEventListener('pointerenter', (event) => {
        if (!canUseHoverNav(event)) {
          return;
        }

        clearHoverClose(nav);
        openDropdown(nav, trigger);
      });
      trigger.addEventListener('keydown', (event) => {
        if (!canUseDropdownNav() || event.key !== 'ArrowDown') return;

        event.preventDefault();
        openDropdown(nav, trigger);
        getDropdown(trigger)?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus({
          preventScroll: true
        });
      });
    });

    nav.querySelectorAll('[data-site-nav-dropdown]').forEach((dropdown) => {
      dropdown.addEventListener('pointerenter', (event) => {
        if (canUseHoverNav(event)) {
          clearHoverClose(nav);
        }
      });
      dropdown.addEventListener('pointerleave', (event) => {
        if (canUseHoverNav(event)) {
          scheduleHoverClose(nav);
        }
      });
    });

    nav.querySelectorAll('[data-site-nav-dropdown] a').forEach((link) => {
      link.addEventListener('click', () => closeDropdowns(nav));
    });

    nav.querySelectorAll('[data-site-nav-products-grid]').forEach((grid) => {
      grid.addEventListener('scroll', () => queueDropdownProductsUpdate(grid.closest('[data-site-nav-dropdown]')), {
        passive: true
      });
      grid.addEventListener('wheel', (event) => handleProductWheel(event, grid), { passive: false });
    });

    if ('ResizeObserver' in window) {
      if (!navResizeObserver) {
        navResizeObserver = new ResizeObserver(() => {
          navs.forEach((item) => {
            queueUpdateNav(item);
            queueOpenDropdownProductsUpdate(item);
          });
        });
      }

      navResizeObserver.observe(nav);
      nav.querySelectorAll('.site-nav__scroller, .site-nav__inner').forEach((item) => navResizeObserver.observe(item));
    } else {
      window.addEventListener('resize', () => {
        queueUpdateNav(nav);
        queueOpenDropdownProductsUpdate(nav);
      }, { passive: true });
    }

    bindDocumentListeners();
    queueUpdateNav(nav);
  };

  const initHeader = (header) => {
    if (headers.has(header)) {
      return;
    }

    headers.add(header);
    const newsletterModal = getNewsletterModal(header);

    header.querySelector('[data-header-v2-menu-toggle]')?.addEventListener('click', () => {
      const drawer = header.querySelector('[data-header-v2-drawer]');
      setDrawerOpen(header, Boolean(drawer?.hidden));
    });

    header.querySelector('[data-header-v2-search-toggle]')?.addEventListener('click', () => {
      const panel = header.querySelector('[data-header-v2-mobile-search]');
      setMobileSearchOpen(header, Boolean(panel?.hidden), true);
    });

    header.querySelectorAll('[data-header-v2-mobile-search] [data-header-search-close]').forEach((control) => {
      control.addEventListener('click', () => setMobileSearchOpen(header, false, true));
    });

    header.querySelectorAll('[data-header-v2-drawer-close]').forEach((control) => {
      control.addEventListener('click', () => setDrawerOpen(header, false, true));
    });

    newsletterModal?.querySelectorAll('[data-header-v2-newsletter-close]').forEach((control) => {
      control.addEventListener('click', () => setNewsletterModalOpen(header, false, true));
    });

    newsletterModal?.addEventListener('keydown', (event) => trapNewsletterFocus(event, newsletterModal));

    header.querySelectorAll('.site-header-v2__drawer-nav a').forEach((link) => {
      link.addEventListener('click', () => setDrawerOpen(header, false));
    });

    header.querySelectorAll('[data-header-v2-drawer-item]').forEach(initDrawerItem);
    header.querySelectorAll('.site-nav').forEach(initNav);
    bindStickyHeader(header);

    if (newsletterModal?.querySelector('[data-header-v2-newsletter-feedback]')) {
      setNewsletterModalOpen(header, true);
    }
  };

  const init = (root = document) => {
    const rootElement = root.nodeType === 1 ? root : null;
    const headersToInit = new Set();

    if (rootElement?.matches('[data-header-v2]')) {
      headersToInit.add(rootElement);
    }

    root.querySelectorAll?.('[data-header-v2]').forEach((header) => headersToInit.add(header));
    headersToInit.forEach(initHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
